import Count from "@/components/Count";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  Target,
  Users,
  Lightbulb,
  Award,
  Handshake,
  ChevronRight,
} from "lucide-react";
import SEO from "@/components/SEO";

const Missions = () => {
  const missions = [
    {
      icon: <Users className="w-8 h-8" />,
      title: "Accompagnement des Artisans",
      description:
        "Accompagner les artisans pour mieux répondre aux exigences de la commande publique, par la formation, le perfectionnement, le financement et l'accès au foncier.",
      color: "bg-blue-100 text-blue-600",
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Coordination et Suivi",
      description:
        "Coordonner et suivre l'évolution de l'accès des artisans à la commande publique pour assurer une croissance équitable.",
      color: "bg-green-100 text-green-600",
    },
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title: "Investissement Créatif",
      description:
        "Confirmer l'investissement du génie créateur sénégalais dans le développement économique du pays.",
      color: "bg-yellow-100 text-yellow-600",
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Vulgarisation du Projet",
      description:
        "Vulgariser le projet du mobilier national par la reproduction des prototypes déjà existants.",
      color: "bg-purple-100 text-purple-600",
    },
    {
      icon: <Handshake className="w-8 h-8" />,
      title: "Innovation Continue",
      description:
        "Créer de nouveaux prototypes par l'organisation d'un Concours du Mobilier National.",
      color: "bg-red-100 text-red-600",
    },
  ];

  return (
    <>
      <SEO
        title="Missions"
        description="Découvrez les missions du Projet Mobilier National : modernisation de l'artisanat, accompagnement des artisans, valorisation du patrimoine artisanal sénégalais."
        keywords="PMN, Missions, Artisanat, Modernisation, Accompagnement, Patrimoine, Artisans, Sénégal"
        ogImage="/images/vision_mission.webp"
      />

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative">
          <div className="absolute w-full inset-0 h-[80vh] bg-black/50 opacity-50" />
          <div className="absolute inset-0">
            <img
              src="/images/vision_mission.webp"
              alt="Mission du PMN"
              className="w-full h-[80vh] object-cover shadow-2xl brightness-[0.85]"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-transparent" />
          </div>
          <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 py-32">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Mission du PMN
              </h1>
              <p className="text-xl text-white/90">
                Notre engagement pour l'innovation et le développement de
                l'artisanat sénégalais
              </p>
            </motion.div>
          </div>
        </section>

        {/* Mission Statement Section */}
        <section className="py-20 bg-[#1E3D1A]/80">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h2 className="text-3xl font-bold text-white/85 mb-8">
                
              </h2>
              <p className="text-lg text-white/85 leading-relaxed">
                
              </p>
            </motion.div>
          </div>
        </section>

        {/* Missions Grid */}
        <section className="py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {missions.map((mission, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300"
                >
                  <div
                    className={`${mission.color} w-16 h-16 rounded-full flex items-center justify-center mb-6`}
                  >
                    {mission.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {mission.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {mission.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Impact Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Notre Impact
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-white p-6 rounded-xl shadow-lg">
                  <div className="text-4xl font-bold text-[#1E3D1A] mb-2">
                    <Count numToCount={10000} />
                  </div>
                  <div className="text-gray-600">Artisans Formés</div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg">
                  <div className="text-4xl font-bold text-[#1E3D1A] mb-2">
                    <Count numToCount={5000} />
                  </div>
                  <div className="text-gray-600">Prototypes Créés</div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg">
                  <div className="text-4xl font-bold text-[#1E3D1A] mb-2">
                    <Count numToCount={15000} />
                  </div>
                  <div className="text-gray-600">Commandes Traitées</div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="max-w-4xl mx-auto text-center bg-[#1E3D1A]/80 p-12 rounded-2xl"
            >
              <h2 className="text-3xl font-bold text-white mb-6">
                Rejoignez Notre Mission
              </h2>
              <p className="text-lg text-white mb-8">
                Ensemble, construisons un avenir meilleur pour l'artisanat
                sénégalais. Découvrez comment vous pouvez contribuer à notre
                mission.
              </p>
              <a href="/contact">
                <Button
                  variant="outline"
                  className="border-white/20 bg-transparent hover:bg-transparent text-white px-4 py-2 rounded hover:text-white/70"
                >
                  En savoir plus <ChevronRight className="ml-2" />
                </Button>
              </a>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Missions;
